<template>
  <ModalDialog
    header-text="Add Products to Cart"
    contentClass="copy-modal-content"
    :canBeClosed="!copyInProgress"
    @closeModal="closeModal"
    open
  >
    <template v-slot:content>
      <DownloadsPlantGrid :plants="certifiedPlants" />
      <Button
        type="button"
        theme-color="primary"
        :disabled="!anyPlantSelected || copyInProgress"
        @click="addToCartClicked"
        data-cy="submit-button"
        >Add to Cart</Button
      >
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import type { IdAndName } from "@/Domain/IdAndName";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import DownloadsPlantGrid from "./DownloadsPlantGrid.vue";
import type { PlantWithSelection } from "@/api/scheduleB";
import { computed, ref } from "vue";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useScheduleBStore } from "@/stores/useScheduleBStore";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";

const main = useMainStore();
const scheduleStore = useScheduleBStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  plants: IdAndName[];
  productIds: number[];
}>();

const emit = defineEmits<{
  (e: "closeModal", cartCount: number): void;
}>();

const copyInProgress = ref(false);

const certifiedPlants = computed(() => {
  return props.plants.filter(
    (p) => p.Status == EnumCompanyStatus.Certified
  ) as PlantWithSelection[];
});

const anyPlantSelected = computed(() => {
  return certifiedPlants.value.some((p) => p.selected);
});

const addToCopyCartAsync = async function () {
  if (copyInProgress.value) return;

  copyInProgress.value = true;

  await handleResponsePromiseAsync(
    api.get("VueAddProductToCart", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication: SubApplicationType.Copy.toString(),
      prId: props.productIds.join(";"),
      terminationDate: "",
      ownsId: certifiedPlants.value
        .filter((p) => p.selected)
        .map((p) => p.ID)
        .join(";"),
      plantName: "",
    }),
    (data) => {
      closeModal(true, data);
    },
    "CopyProductsModal.vue",
    "addToCopyCartAsync",
    (error) => {
      console.log(error);
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: "Failed to add products to the cart",
        duration: 5000,
      });
    }
  );

  copyInProgress.value = false;
};

const addToCartClicked = function () {
  addToCopyCartAsync();
};

const closeModal = function (closed: boolean, cartCount: number = -1) {
  if (!closed) {
    notificationStore.notify({
      type: NotificationTypeEnum.info,
      message: "Please wait while products are being added to the cart",
      duration: 5000,
    });
    return;
  }
  if (cartCount > 0) {
    notificationStore.notify({
      type: NotificationTypeEnum.success,
      message: `Products added to the cart. ${cartCount} product(s) in the cart.`,
      duration: 5000,
    });
    scheduleStore.setCartCount(SubApplicationType.Copy, cartCount);
  }
  emit("closeModal", cartCount);
};
</script>

<style lang="scss">
.copy-modal-content {
  max-width: 600px;
  padding: 12px;

  .plant-grid {
    max-height: 360px;
    margin-bottom: 20px;
  }
}
</style>
